import { addDays, compareDates, diffDays, eachDay, isoWeekday, type IsoDate } from './dates';
import { MEALS, mealsOf, type KitchenCalendar, type Meal, type Pause, type Slot, type Terms } from './types';
import { planCalendar } from './weekdays';

/**
 * The end-date engine. Everything the kitchen promises a customer comes from
 * here: how many meals are left, which day the plan ends, what a pause, a moved
 * meal, a missed delivery or a holiday does to that date.
 *
 * Model: a plan is a number of *meals*. `mealDays` days of a plan with N meals
 * a day (breakfast, lunch and/or dinner) are N x mealDays meals. Each delivery
 * day (a weekday the plan delivers on, or the kitchen's when the plan has no days
 * of its own; not a holiday) offers the plan's meals,
 * minus any the customer paused or moved. The plan ends on the day its last
 * meal is delivered. Pausing therefore pushes the end date out, pausing one of
 * a plan's three daily meals pushes it by a third of a day's worth, and moving
 * a meal to another day does not move the end date at all.
 *
 * `scheduleOf` lists every meal of a plan; everything else is derived from it,
 * so the end date, the counts, the calendar and the kitchen list always agree.
 */

export const MAX_HORIZON_DAYS = 800;

/** Yes/no for each meal on one day. */
export type MealSet = Record<Meal, boolean>;

const none = (): MealSet => ({ breakfast: false, lunch: false, dinner: false });
const EMPTY: MealSet = Object.freeze(none());

export function totalMeals(terms: Pick<Terms, 'mealDays' | 'slot'>): number {
  return terms.mealDays * mealsOf(terms.slot).length;
}

export function holidaySet(h: KitchenCalendar['holidays']): ReadonlySet<IsoDate> {
  return h instanceof Set ? h : new Set(h as readonly IsoDate[]);
}

/** Index pauses by day -> the meals paused that day. */
export function pauseIndex(pauses: readonly Pause[]): Map<IsoDate, MealSet> {
  const idx = new Map<IsoDate, MealSet>();
  for (const p of pauses) {
    const cur = idx.get(p.day) ?? none();
    for (const m of mealsOf(p.slot)) cur[m] = true;
    idx.set(p.day, cur);
  }
  return idx;
}

/** True when the kitchen delivers anything at all on this day. */
export function isDeliveryDay(day: IsoDate, cal: KitchenCalendar): boolean {
  return cal.deliveryDays.includes(isoWeekday(day)) && !holidaySet(cal.holidays).has(day);
}

/** The plan meals offered on `day` once pauses are taken off (ignores plan start/end). */
export function mealsOnDay(
  day: IsoDate,
  slot: Slot,
  cal: KitchenCalendar,
  paused: Map<IsoDate, MealSet>,
): MealSet {
  if (!isDeliveryDay(day, cal)) return EMPTY;
  const wanted = mealsOf(slot);
  const p = paused.get(day);
  const out = none();
  for (const m of wanted) out[m] = !(p?.[m] ?? false);
  return out;
}

export function countMeals(m: MealSet): number {
  return MEALS.reduce((n, meal) => n + (m[meal] ? 1 : 0), 0);
}

export class EngineError extends Error {}

/** One meal on one day. */
export interface MealRef {
  day: IsoDate;
  meal: Meal;
}

/**
 * The plan's meal on `from` is not delivered; one meal is delivered on `to` instead.
 * `to` can be any meal the kitchen serves, on a delivery day, even a little after the plan's natural end.
 */
export interface MealMove {
  from: MealRef;
  to: MealRef;
}

/** A meal the kitchen could not deliver, and the day that was marked. */
export interface MissedMeal extends MealRef {
  /**
   * The owed meal is never put on a day that had already gone when the miss was marked
   * (say the plan had ended and a meal from last week is marked as not delivered).
   */
  markedOn?: IsoDate;
}

/** Everything that changes when a plan's meals arrive. */
export interface PlanChanges {
  pauses?: readonly Pause[];
  moves?: readonly MealMove[];
  /** Meals the kitchen could not deliver. They do not count, so the plan owes them at the end. */
  missed?: readonly MissedMeal[];
  /** Today. A meal moved to a day that has gone was delivered, whatever changed in the kitchen since. */
  today?: IsoDate;
  /** The last day this subscription may deliver meals. Later meals stay in the history but are not scheduled. */
  closedOn?: IsoDate;
}

/** Days after `after` and before `before` that had already gone without a meal when a miss was marked. */
interface Gap {
  after: IsoDate;
  before: IsoDate;
}

/** One meal of a plan: from the plan itself, moved in from another day, or scheduled but missed (not counted). */
export interface Delivery {
  day: IsoDate;
  meal: Meal;
  kind: 'plan' | 'moved' | 'missed';
}

const refKey = (r: MealRef) => `${r.day}|${r.meal}`;
const byDayAndMeal = (a: MealRef, b: MealRef) => compareDates(a.day, b.day) || MEALS.indexOf(a.meal) - MEALS.indexOf(b.meal);

/**
 * Every meal the plan delivers, in order.
 *
 * - Paused meals, and meals moved away, are skipped on their day.
 * - A moved meal arrives on its new day and meal. It is owed at the end of the plan instead when that day stops
 *   being a delivery day (a holiday, a change of delivery days), when the kitchen stops serving that meal, or when
 *   the customer pauses that meal or the whole day there. Nothing is lost.
 * - Missed meals stay on their day (so their mark stays visible) but do not count.
 * - Regular meals fill whatever is left, in serving order, so the last day carries only the meals still owed.
 */
export function scheduleOf(terms: Terms, kitchen: KitchenCalendar, changes: PlanChanges = {}): Delivery[] {
  if (terms.mealDays < 1) throw new EngineError('A plan needs at least one meal day');
  // The plan's own weekdays; moved meals may also land on any day the kitchen delivers.
  const cal = planCalendar(terms, kitchen);
  if (cal.deliveryDays.length === 0) throw new EngineError('The kitchen has no delivery days');
  // Misses in the order they were marked: each one owes a meal after the plan's end as it stood then, but never
  // on a day that had already gone by the time it was marked. Those days stay empty for every later rebuild.
  const missed = [...(changes.missed ?? [])].sort((a, b) => compareDates(a.markedOn ?? a.day, b.markedOn ?? b.day));
  const gaps: Gap[] = [];
  for (let i = 0; i < missed.length; i++) {
    const on = missed[i]!.markedOn;
    if (!on) continue;
    const end = lastDay(build(terms, cal, kitchen, changes, missed.slice(0, i), gaps), terms.startDate);
    if (compareDates(on, end) > 0) gaps.push({ after: end, before: on });
  }
  const schedule = build(terms, cal, kitchen, changes, missed, gaps);
  return changes.closedOn ? schedule.filter((d) => compareDates(d.day, changes.closedOn!) <= 0) : schedule;
}

function build(
  terms: Terms, cal: KitchenCalendar, kitchen: KitchenCalendar, changes: PlanChanges, missedMeals: readonly MealRef[], gaps: readonly Gap[],
): Delivery[] {
  const moves = changes.moves ?? [];
  const paused = pauseIndex([...(changes.pauses ?? []), ...moves.map((m) => ({ day: m.from.day, slot: m.from.meal }))]);
  const missed = new Set(missedMeals.map(refKey));

  const out = movedIn(terms, cal, kitchen, changes, missed);
  const need = totalMeals(terms) - out.filter((d) => d.kind === 'moved').length;

  const planMeals = mealsOf(terms.slot);
  let counted = 0;
  let day = terms.startDate;
  let days = 0;
  while (counted < need) {
    if (days++ >= MAX_HORIZON_DAYS) throw new EngineError('Plan does not end within the supported horizon');
    const skipped = gaps.some((g) => compareDates(day, g.after) > 0 && compareDates(day, g.before) < 0);
    if (!skipped && isDeliveryDay(day, cal)) {
      const off = paused.get(day);
      for (const meal of planMeals) {
        if (counted === need || off?.[meal]) continue;
        const isMissed = missed.has(refKey({ day, meal }));
        out.push({ day, meal, kind: isMissed ? 'missed' : 'plan' });
        if (!isMissed) counted++;
      }
    }
    day = addDays(day, 1);
  }
  return out.sort(byDayAndMeal);
}

/**
 * Meals moved in from other days that still stand (see scheduleOf), earliest first.
 * Never more than the plan has: if the plan is edited shorter, the latest moved meals drop out.
 */
function movedIn(terms: Terms, cal: KitchenCalendar, kitchen: KitchenCalendar, changes: PlanChanges, missed: ReadonlySet<string>): Delivery[] {
  const realPauses = pauseIndex(changes.pauses ?? []);
  const planMeals = mealsOf(terms.slot);
  const stands = ({ to }: MealMove) => {
    if (compareDates(to.day, terms.startDate) < 0) return false;
    // Already delivered: a later change of meals, delivery days or holidays does not undo it.
    if (changes.today && compareDates(to.day, changes.today) < 0) return true;
    const off = realPauses.get(to.day);
    const away = !!off && (off[to.meal] || planMeals.every((m) => off[m]));
    const served = !cal.meals || cal.meals.includes(to.meal);
    // A meal can move to one of the plan's days or to any day the kitchen delivers.
    return (isDeliveryDay(to.day, cal) || isDeliveryDay(to.day, kitchen)) && served && !away;
  };

  const out: Delivery[] = [];
  let counted = 0;
  const total = totalMeals(terms);
  for (const { to } of [...(changes.moves ?? [])].filter(stands).sort((a, b) => byDayAndMeal(a.to, b.to))) {
    const isMissed = missed.has(refKey(to));
    if (!isMissed && counted === total) continue;
    out.push({ day: to.day, meal: to.meal, kind: isMissed ? 'missed' : 'moved' });
    if (!isMissed) counted++;
  }
  return out;
}

/** The day of the last meal that counts. */
function lastDay(schedule: readonly Delivery[], fallback: IsoDate): IsoDate {
  for (let i = schedule.length - 1; i >= 0; i--) if (schedule[i]!.kind !== 'missed') return schedule[i]!.day;
  return fallback;
}

/**
 * The day the plan's last meal is delivered.
 * Throws if the kitchen has no delivery days (the plan could never end).
 */
export function computeEndDate(terms: Terms, cal: KitchenCalendar, changes: PlanChanges = {}): IsoDate {
  return lastDay(scheduleOf(terms, cal, changes), terms.startDate);
}

/** The end date if nobody ever paused or moved a meal (holidays still count). */
export function computeOriginalEndDate(terms: Terms, cal: KitchenCalendar): IsoDate {
  return computeEndDate(terms, cal, {});
}

export interface PlanSummary {
  startDate: IsoDate;
  endDate: IsoDate;
  /** End date without any pauses or moves. */
  originalEndDate: IsoDate;
  totalMeals: number;
  /** Meals delivered before `today`. */
  delivered: number;
  /** Meals not yet delivered (today's included). */
  left: number;
  /** Delivery days inside the plan with at least one paused meal (these push the end date out). */
  pausedDays: number;
  /** Meals moved to another day or meal (these do not move the end date). */
  movedMeals: number;
  /** Meals the kitchen could not deliver (owed at the end). */
  missedMeals: number;
  /** Calendar days from today to the end date (negative once ended). */
  daysToEnd: number;
  state: 'upcoming' | 'active' | 'ended' | 'closed';
}

export function summarise(terms: Terms, kitchen: KitchenCalendar, changes: PlanChanges, today: IsoDate): PlanSummary {
  const cal = planCalendar(terms, kitchen);
  const schedule = scheduleOf(terms, kitchen, { ...changes, today: changes.today ?? today });
  const endDate = lastDay(schedule, terms.startDate);
  const total = totalMeals(terms);
  const counted = schedule.filter((d) => d.kind !== 'missed');
  const delivered = Math.min(counted.filter((d) => compareDates(d.day, today) < 0).length, total);

  const planMeals = mealsOf(terms.slot);
  let pausedDays = 0;
  for (const [day, off] of pauseIndex(changes.pauses ?? [])) {
    const inPlan = compareDates(day, terms.startDate) >= 0 && compareDates(day, endDate) <= 0;
    if (inPlan && isDeliveryDay(day, cal) && planMeals.some((m) => off[m])) pausedDays++;
  }

  let state: PlanSummary['state'] = changes.closedOn ? 'closed' : 'active';
  if (!changes.closedOn && compareDates(today, terms.startDate) < 0) state = 'upcoming';
  else if (!changes.closedOn && compareDates(today, endDate) > 0) state = 'ended';

  return {
    startDate: terms.startDate,
    endDate,
    originalEndDate: computeOriginalEndDate(terms, kitchen),
    totalMeals: total,
    delivered,
    left: state === 'closed' || state === 'ended' ? 0 : Math.max(0, total - delivered),
    pausedDays,
    movedMeals: changes.moves?.length ?? 0,
    missedMeals: schedule.length - counted.length,
    daysToEnd: diffDays(today, endDate),
    state,
  };
}

export type DayState =
  | 'none' // outside the plan
  | 'off' // not one of the plan's days (say Sunday), or a holiday
  | 'holiday'
  | 'delivered'
  | 'today'
  | 'paused' // nothing that day: every meal paused or moved away
  | 'partial' // some meals paused or moved away
  | 'upcoming'
  | 'end'; // last delivery day

export interface CalendarCell {
  date: IsoDate;
  state: DayState;
  /** Meals paused that day, when state is paused/partial. */
  pausedMeals: Meal[];
  /** Meals moved away from this day. */
  movedOut: Meal[];
  /** Meals moved here from another day. */
  movedIn: Meal[];
}

/** State of each day for a month calendar. */
export function monthCells(
  month: string,
  terms: Terms,
  kitchen: KitchenCalendar,
  changes: PlanChanges,
  today: IsoDate,
  endDate: IsoDate,
): CalendarCell[] {
  const cal = planCalendar(terms, kitchen);
  const first = `${month}-01`;
  const [y, m] = month.split('-').map(Number) as [number, number];
  const last = addDays(`${new Date(Date.UTC(y, m, 1)).toISOString().slice(0, 7)}-01`, -1);
  const paused = pauseIndex(changes.pauses ?? []);
  const holidays = holidaySet(cal.holidays);
  const wanted = mealsOf(terms.slot);

  const arriving = new Map<IsoDate, Delivery[]>();
  for (const d of scheduleOf(terms, kitchen, { ...changes, today: changes.today ?? today })) arriving.set(d.day, [...(arriving.get(d.day) ?? []), d]);
  const leaving = new Map<IsoDate, Meal[]>();
  for (const mv of changes.moves ?? []) leaving.set(mv.from.day, [...(leaving.get(mv.from.day) ?? []), mv.from.meal]);

  return eachDay(first, last).map((date): CalendarCell => {
    const here = arriving.get(date) ?? [];
    const cell = {
      date,
      pausedMeals: wanted.filter((mm) => paused.get(date)?.[mm] ?? false),
      movedOut: leaving.get(date) ?? [],
      movedIn: here.filter((d) => d.kind === 'moved').map((d) => d.meal),
    };
    const inPlan = compareDates(date, terms.startDate) >= 0 && compareDates(date, endDate) <= 0;
    if (!inPlan) return { ...cell, state: 'none' };
    if (holidays.has(date)) return { ...cell, state: 'holiday' };
    // A day off the plan stays off, unless a meal was moved there.
    if (!isDeliveryDay(date, cal) && here.length === 0) return { ...cell, state: 'off' };

    const skipped = cell.pausedMeals.length + cell.movedOut.length > 0;
    // Nothing arrives: everything was paused or moved, or the plan's regular meals ended before a later moved meal.
    if (here.length === 0) return { ...cell, state: skipped ? 'paused' : 'none' };
    if (date === endDate) return { ...cell, state: 'end' };
    if (skipped) return { ...cell, state: 'partial' };
    if (date === today) return { ...cell, state: 'today' };
    return { ...cell, state: compareDates(date, today) < 0 ? 'delivered' : 'upcoming' };
  });
}

export interface ShiftSettlement {
  isMidCycle: boolean;
  closedOn: IsoDate;
  consumedMeals: number;
  totalMeals: number;
  proratedCost: number;
  paid: number;
  credit: number;
}

/**
 * Computes financial and schedule settlement when shifting a subscriber mid-cycle
 * to a new plan or slot.
 *
 * - Old plan deliveries stop on `closedOn` (the day before `newStartDate`).
 * - Meals delivered on or before `closedOn` count as consumed.
 * - Prorated cost is proportional to consumed meals / total meals.
 * - Unconsumed credit (net paid minus prorated cost) transfers to the new plan.
 */
export function computeShiftSettlement(opts: {
  curTerms: Terms;
  curChanges: PlanChanges;
  curPrice: number;
  curPaid: number;
  curRefunded: number;
  newStartDate: IsoDate;
  calendar: KitchenCalendar;
}): ShiftSettlement {
  const closedOn = addDays(opts.newStartDate, -1);
  const total = totalMeals(opts.curTerms);
  let consumedMeals = 0;
  if (compareDates(closedOn, opts.curTerms.startDate) >= 0) {
    const closedSchedule = scheduleOf(opts.curTerms, opts.calendar, { ...opts.curChanges, closedOn });
    consumedMeals = closedSchedule.filter((d) => d.kind !== 'missed').length;
  }
  const proratedCost = total > 0 ? Math.min(opts.curPrice, Math.round(opts.curPrice * (consumedMeals / total))) : 0;
  const netPaid = Math.max(0, opts.curPaid - opts.curRefunded);
  const credit = Math.max(0, netPaid - proratedCost);

  return {
    isMidCycle: true,
    closedOn,
    consumedMeals,
    totalMeals: total,
    proratedCost,
    paid: netPaid,
    credit,
  };
}
